// OPEN FORM modal for a single Section 2
// certification. Mirrors the Section-1 <CriteriaChecklistItemPopover>:
// the modal owns LOCAL form state for every editable field and only
// touches the server on Save and close (the SaveCertificationState
// endpoint, a non-terminal upsert).
//
// Cloned rather than reused: <CriteriaChecklistItemPopover> is built
// around CriteriaChecklistItemDto + the catalog formSchema engine +
// escalation directive flow. This modal carries only the three universal
// fields Section 2 needs — Severity Assessment, Report Uploaded
// Confirmation, Justification — kept visually identical to Section 1.

import { useEffect, useReducer, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { certificationTemplatesApi } from "@/api/dealFlow/client";
import { useSaveCertificationState } from "@/hooks/dealFlow/useDealFlow";
import {
  reportGenerateDeepLink,
  useReportItemPrefill,
} from "@/hooks/reports/useReportItemPrefill";
import { useDateFormatter } from "@/utils/datetime";
import { ReportUploadedAttestationField } from "@/components/DealFlow/common/ReportUploadedAttestationField";
import {
  CRITERIA_ITEM_ASSESSMENTS,
  type CriteriaItemAssessment,
  type DealFlowTab,
  type UnderwritingCertificationDto,
} from "@/api/dealFlow/types";
import type { CertConfig } from "./types";

interface CertificationItemModalProps {
  open: boolean;
  onClose: () => void;
  dealId: string;
  tab: DealFlowTab;
  /** Certification section. Defaults to 2 (analyst Additional
   *  Certifications); the Closing tab Step 1 certs pass 1. */
  section?: number;
  cert: CertConfig;
  row: UnderwritingCertificationDto | null;
}

type CertFormState = {
  assessment: CriteriaItemAssessment | null;
  reportAttested: boolean;
  justification: string;
};

type CertFormAction =
  | { type: "init"; from: UnderwritingCertificationDto | null }
  | { type: "setAssessment"; value: CriteriaItemAssessment | null }
  | { type: "setReportAttested"; value: boolean }
  | { type: "setJustification"; value: string };

function seed(row: UnderwritingCertificationDto | null): CertFormState {
  return {
    assessment: row?.assessment ?? null,
    reportAttested: row?.reportUploadedAttested ?? false,
    justification: row?.justification ?? "",
  };
}

function reducer(state: CertFormState, action: CertFormAction): CertFormState {
  switch (action.type) {
    case "init":
      return seed(action.from);
    case "setAssessment":
      return { ...state, assessment: action.value };
    case "setReportAttested":
      return { ...state, reportAttested: action.value };
    case "setJustification":
      return { ...state, justification: action.value };
  }
}

export function CertificationItemModal({
  open,
  onClose,
  dealId,
  tab,
  section = 2,
  cert,
  row,
}: CertificationItemModalProps) {
  const toast = useToast();
  const save = useSaveCertificationState(dealId);

  // The analyst-facing in-depth prose (Description + RequiredEvidence)
  // lives in the admin-editable UnderwritingCertificationTemplate table, keyed
  // by certification code. Edits are rare; useUpdateCertificationTemplate
  // invalidates this query.
  const templateQ = useQuery({
    queryKey: ["deal-flow", "cert-template", cert.certificationCode],
    queryFn: () => certificationTemplatesApi.get(cert.certificationCode),
    enabled: open,
    staleTime: 1000 * 60 * 60 * 24,
  });

  const [form, dispatch] = useReducer(reducer, row, seed);

  // Item-level "Generate report" shortcut — Section-2 analyst certs only
  // (the Closing tab reuses this modal with section=1 certs that have no
  // report templates). Resolution is role-filtered server-side; no match →
  // no button. Convenience link only: it never touches the analyst form or
  // the Report Uploaded Confirmation semantics.
  const reportPrefill = useReportItemPrefill(
    cert.certificationCode,
    dealId,
    open && section === 2,
  );

  // Local state is seeded once per modal-open cycle. A background refetch
  // of the parent list query MUST NOT clobber in-progress edits — re-seed
  // only when a different cert row is opened.
  const seededForCodeRef = useRef<string>(cert.certificationCode);
  useEffect(() => {
    if (cert.certificationCode !== seededForCodeRef.current) {
      dispatch({ type: "init", from: row });
      seededForCodeRef.current = cert.certificationCode;
    }
  }, [cert.certificationCode, row]);

  // Status decides editability: once a terminal transition has been
  // recorded the form is locked (Open form still surfaces it read-back).
  const status = row?.status ?? "Pending";
  const readOnly =
    status === "Certified" || status === "Declined" || status === "Escalated";

  const handleSave = async () => {
    try {
      await save.mutateAsync({
        tab,
        section,
        certificationCode: cert.certificationCode,
        label: cert.title,
        assessment: form.assessment,
        reportUploadedAttested: form.reportAttested,
        justification:
          form.justification.trim().length > 0 ? form.justification : null,
      });
      toast.success("Certification form saved.");
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`${cert.certificationCode} — ${cert.title}`}
      description={cert.description}
      size="lg"
      footer={
        <>
          {section === 2 && reportPrefill.data && (
            <a
              href={reportGenerateDeepLink(
                reportPrefill.data.templateId,
                dealId,
                cert.certificationCode,
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="mr-auto inline-flex items-center gap-space-1 text-small font-semibold text-onyx hover:text-gold-shadow"
            >
              <ExternalLink size={14} /> Generate report
            </a>
          )}
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={save.isPending}
          >
            Close
          </Button>
          {!readOnly && (
            <Button
              variant="primary"
              onClick={() => void handleSave()}
              disabled={save.isPending}
            >
              {save.isPending ? "Saving…" : "Save and close"}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-space-4">
        {templateQ.isLoading ? (
          <div
            data-testid="cert-template-skeleton"
            className="flex flex-col gap-space-2"
          >
            <div className="h-4 bg-white-warm animate-pulse rounded-md w-1/3" />
            <div className="h-20 bg-white-warm animate-pulse rounded-md" />
            <div className="h-4 bg-white-warm animate-pulse rounded-md w-1/4" />
            <div className="h-16 bg-white-warm animate-pulse rounded-md" />
          </div>
        ) : templateQ.isError || !templateQ.data ? (
          <div className="text-status-error">
            Couldn&apos;t load the certification template prose for{" "}
            {cert.certificationCode}. The action buttons still work, but the
            in-depth description is unavailable.
          </div>
        ) : (
          <>
            <section>
              <h4 className="font-display text-label uppercase tracking-label text-gold-shadow m-0">
                What you&apos;re certifying
              </h4>
              <p className="text-small text-onyx mt-space-1 whitespace-pre-line">
                {templateQ.data.description}
              </p>
            </section>
            <section>
              <h4 className="font-display text-label uppercase tracking-label text-gold-shadow m-0">
                What you&apos;ll need to produce
              </h4>
              <p className="text-small text-onyx mt-space-1 whitespace-pre-line">
                {templateQ.data.requiredEvidence}
              </p>
            </section>
          </>
        )}

        <section>
          <h4 className="font-display text-label uppercase tracking-label text-gold-shadow m-0 mb-space-2">
            Analyst form
          </h4>
          <div className="flex flex-col gap-space-3">
            <SeverityAssessmentField
              value={form.assessment}
              onChange={(v) => dispatch({ type: "setAssessment", value: v })}
              disabled={readOnly}
            />
            <ReportUploadedAttestationField
              checked={form.reportAttested}
              persistedAttested={row?.reportUploadedAttested ?? false}
              persistedDisplayName={
                row?.reportUploadedAttestedByDisplayName ?? null
              }
              persistedAttestedAt={row?.reportUploadedAttestedAt ?? null}
              onChange={(v) =>
                dispatch({ type: "setReportAttested", value: v })
              }
              disabled={readOnly}
            />
            <JustificationField
              value={form.justification}
              onChange={(v) => dispatch({ type: "setJustification", value: v })}
              disabled={readOnly}
            />
          </div>
        </section>

        {status === "Certified" && row && (
          <TerminalStamp
            label="Certified"
            who={row.certifiedByDisplayName}
            when={row.certifiedAt}
          />
        )}
        {status === "Declined" && row && (
          <>
            <TerminalStamp
              label="Declined"
              who={row.declinedByDisplayName}
              when={row.declinedAt}
            />
            {row.declineReason && (
              <p className="text-caption text-status-error m-0">
                Decline reason: {row.declineReason}
              </p>
            )}
          </>
        )}
        {status === "Escalated" && row && (
          <>
            <TerminalStamp
              label="Escalated"
              who={row.escalatedByDisplayName}
              when={row.escalatedAt}
            />
            {row.escalationReason && (
              <p className="text-caption text-gray-mid italic m-0">
                Escalation reason: {row.escalationReason}
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

const JUSTIFICATION_MAX = 2000;

function SeverityAssessmentField({
  value,
  onChange,
  disabled,
}: {
  value: CriteriaItemAssessment | null;
  onChange: (next: CriteriaItemAssessment | null) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-space-1">
      <label
        htmlFor="cert-severity-assessment"
        className="font-display text-label uppercase tracking-label text-gold-shadow m-0"
      >
        Severity assessment <span className="text-status-error">*</span>
      </label>
      <select
        id="cert-severity-assessment"
        className="px-space-2 py-space-1 border border-border-light rounded-md text-small bg-white-pure disabled:bg-white-warm disabled:text-gray-mid"
        value={value ?? ""}
        onChange={(e) =>
          onChange(
            e.target.value === ""
              ? null
              : (e.target.value as CriteriaItemAssessment),
          )
        }
        disabled={disabled}
      >
        <option value="">Select an assessment…</option>
        {CRITERIA_ITEM_ASSESSMENTS.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      <p className="text-caption text-gray-mid m-0">
        Required to certify this item. Captures the analyst's judgment of how
        serious this finding is.
      </p>
    </div>
  );
}

function JustificationField({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-col gap-space-1">
      <label
        htmlFor="cert-justification"
        className="font-display text-label uppercase tracking-label text-gold-shadow m-0"
      >
        Justification / notes <span className="text-status-error">*</span>
      </label>
      <textarea
        id="cert-justification"
        value={value}
        onChange={(e) => onChange(e.target.value.slice(0, JUSTIFICATION_MAX))}
        rows={6}
        disabled={disabled}
        placeholder="Document what you reviewed and why this certification is satisfied."
        className="w-full border border-border-light rounded-md p-space-2 text-small disabled:bg-white-warm disabled:text-gray-mid"
      />
      <p className="text-caption text-gray-mid m-0">
        Required to certify or decline this item. {value.length}/
        {JUSTIFICATION_MAX} characters.
      </p>
    </div>
  );
}

function TerminalStamp({
  label,
  who,
  when,
}: {
  label: string;
  who: string | null;
  when: string | null;
}) {
  const { dateTime } = useDateFormatter();
  if (!when) return null;
  return (
    <p className="text-caption text-gray-mid italic m-0">
      {label} by {who ?? "Unknown user"} on{" "}
      {dateTime(when)}
    </p>
  );
}
